import React, { type MutableRefObject, type MouseEvent as ReactMouseEvent } from 'react';

import { type CellContext, type ColumnDef, type Row, type Table } from '@tanstack/react-table';

import Checkbox from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled';

const handleRowSelection = <T extends object>({
	row,
	table,
	lastSelectedRowIdRef,
	event,
}: {
	row: Row<T>;
	table: Table<T>;
	lastSelectedRowIdRef?: MutableRefObject<string | null>;
	event: ReactMouseEvent<Element, MouseEvent>;
}): void => {
	event.stopPropagation();

	const wasCurrentRowChecked = row.getIsSelected();

	// Toggle selection of this row
	row.toggleSelected();

	// If shift key is pressed and we have a last selected row, select range
	if (
		lastSelectedRowIdRef &&
		lastSelectedRowIdRef.current !== null &&
		(event.nativeEvent as MouseEvent).shiftKey
	) {
		const rows = table.getRowModel().rows;
		const lastIndex = rows.findIndex((r) => r.id === lastSelectedRowIdRef.current);
		const currentIndex = rows.findIndex((r) => r.id === row.id);

		if (lastIndex !== -1 && currentIndex !== -1) {
			const isLastIndexChecked = rows[lastIndex]?.getIsSelected() ?? false;

			const [start, end] =
				lastIndex < currentIndex ? [lastIndex, currentIndex] : [currentIndex, lastIndex];

			// Toggle selection of all rows between last selected and this one
			// but only if the last selected row is not the same as the current one
			if (wasCurrentRowChecked !== isLastIndexChecked) {
				for (let i = start; i <= end; i++) {
					if (rows[i]) {
						rows[i].toggleSelected(!wasCurrentRowChecked);
					}
				}
			}
		}
	}

	// Record the last selected row id
	if (lastSelectedRowIdRef) {
		lastSelectedRowIdRef.current = row.id;
	}
};

const styles = cssMap({
	checkboxContainer: {
		alignItems: 'center',
		minWidth: '44px',
	},
});

export type ReservedColumnFeature = 'checkbox' | 'dragHandle';

const reservedPrefix = 'charlie-table-reserved-';
export const RESERVED_COLUMN_IDS = {
	CHECKBOX: `${reservedPrefix}checkbox`,
	DRAG_HANDLE: `${reservedPrefix}drag-handle`,
} as const;

export const DEFAULT_FEATURE_SIZES: Record<ReservedColumnFeature, number> = {
	checkbox: 44,
	dragHandle: 32,
};

export interface ReservedColumnOptions {
	id: string;
	feature: ReservedColumnFeature;
	position?: 'start' | 'end';
	customFeatureSizes?: Partial<Record<ReservedColumnFeature, number>>;
	labels?: {
		selectRow?: string;
		selectAllRows?: string;
	};
	analyticsContext?: Record<string, any>;
	lastSelectedRowIdRef?: MutableRefObject<string | null>;
}

export interface ReservedColumnConfig<T> {
	id: string;
	columnDef: ColumnDef<T>;
	enabled: boolean;
	position?: 'start' | 'end';
}

export interface ProcessReservedColumnsOptions<T> {
	/** Array of reserved column configurations */
	reservedColumns: ReservedColumnConfig<T>[];
	/** Original column definitions from props */
	originalColumns?: ColumnDef<T>[];
}

/**
 * Result of processing reserved columns
 */
export interface ReservedColumnResult<T> {
	modifiedColumns: ColumnDef<T>[];
	enabledReservedColumnIds: string[];
	utils: {
		appendReservedColumnsToList: (list?: Array<string>) => Array<string> | undefined;
	};
}

export function processReservedColumns<T extends object>({
	reservedColumns,
	originalColumns = [],
}: ProcessReservedColumnsOptions<T>): ReservedColumnResult<T> {
	const enabledReservedColumns = reservedColumns.filter((config) => config.enabled);
	const enabledReservedColumnIds = enabledReservedColumns.map((config) => config.id);

	// Separate columns by position
	const startColumns = enabledReservedColumns
		.filter((config) => config.position === 'start' || !config.position)
		.map((config) => config.columnDef);

	const endColumns = enabledReservedColumns
		.filter((config) => config.position === 'end')
		.map((config) => config.columnDef);

	// Build modified columns array
	const modifiedColumns = [...startColumns, ...originalColumns, ...endColumns];

	// Utility function to append reserved column IDs to lists
	const appendReservedColumnsToList = (list?: Array<string>): Array<string> | undefined => {
		if (enabledReservedColumnIds.length === 0 || !list) {
			return list;
		}

		const newList = [...list];

		const startColumnIds = enabledReservedColumns
			.filter((config) => config.position === 'start' || !config.position)
			.map((config) => config.id);

		const endColumnIds = enabledReservedColumns
			.filter((config) => config.position === 'end')
			.map((config) => config.id);

		enabledReservedColumnIds.forEach((id) => {
			const index = newList.indexOf(id);
			if (index !== -1) {
				newList.splice(index, 1);
			}
		});

		if (startColumnIds.length > 0) {
			newList.unshift(...startColumnIds);
		}

		if (endColumnIds.length > 0) {
			newList.push(...endColumnIds);
		}

		return newList;
	};

	return {
		modifiedColumns,
		enabledReservedColumnIds,
		utils: {
			appendReservedColumnsToList,
		},
	};
}

export function processColumnStates<T extends object>(
	reservedColumnResult: ReservedColumnResult<T>,
	states: {
		columnOrder?: Array<string>;
		columnPinningLeft?: Array<string>;
	},
	initialStates: {
		columnOrder?: Array<string>;
		columnPinningLeft?: Array<string>;
	} = {},
) {
	const { utils } = reservedColumnResult;

	return {
		modifiedColumnOrder: utils.appendReservedColumnsToList(states.columnOrder),
		modifiedInitialColumnOrder: utils.appendReservedColumnsToList(initialStates.columnOrder),
		modifiedColumnPinningLeft: utils.appendReservedColumnsToList(states.columnPinningLeft),
		modifiedInitialColumnPinningLeft: utils.appendReservedColumnsToList(
			initialStates.columnPinningLeft,
		),
	};
}

export function createReservedColumn<T extends object>({
	id,
	feature,
	position = 'start',
	customFeatureSizes = {},
	labels,
	analyticsContext,
	lastSelectedRowIdRef,
}: ReservedColumnOptions): ReservedColumnConfig<T> {
	const featureSizes = { ...DEFAULT_FEATURE_SIZES, ...customFeatureSizes };

	const totalSize = featureSizes[feature];

	const headerRenderer = ({ table }: { table: Table<T> }) => {
		if (feature === 'checkbox') {
			return (
				<Checkbox
					analyticsContext={
						analyticsContext
							? {
									componentName: 'charlieTableSelectAllRowsButton',
									...analyticsContext,
								}
							: undefined
					}
					aria-label={labels?.selectAllRows || 'Select all rows'}
					isIndeterminate={table.getIsSomeRowsSelected()}
					isChecked={table.getIsAllRowsSelected()}
					onClick={() => table.toggleAllRowsSelected()}
				/>
			);
		}
		return <div />;
	};

	const cellRenderer = (context: CellContext<T, unknown>) => {
		const { row, table } = context;
		if (feature === 'checkbox') {
			const isSomeSelected = row.getIsSomeSelected();
			const isAllSubRowsSelected = row.getIsAllSubRowsSelected();
			const isSelected = row.getIsSelected();

			return (
				<Flex xcss={styles.checkboxContainer}>
					<Checkbox
						analyticsContext={
							analyticsContext
								? {
										componentName: 'charlieTableSelectRowButton',
										...analyticsContext,
									}
								: undefined
						}
						aria-label={labels?.selectRow || 'Select row'}
						isDisabled={!row.getCanSelect()}
						isIndeterminate={isSomeSelected && !isAllSubRowsSelected}
						isChecked={isSelected || isAllSubRowsSelected}
						onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
							handleRowSelection({
								row,
								table,
								lastSelectedRowIdRef,
								event: e,
							});
						}}
					/>
				</Flex>
			);
		}
		return <div />;
	};

	const columnDef: ColumnDef<T, unknown> = {
		header: headerRenderer,
		id,
		size: Math.max(totalSize, 32), // Minimum size of 32px
		minSize: Math.max(totalSize, 32),
		enableSorting: false,
		enableResizing: false,
		cell: cellRenderer,
	};

	return {
		id,
		enabled: true,
		position,
		columnDef,
	};
}

export function isReservedColumn(columnId: string): boolean {
	// TODO: Improve this quick and dirty check to see if the column is a reserved column.
	return columnId.startsWith(reservedPrefix);
}

export function getFirstNonReservedColumnIndex(columns: Array<{ id: string }>): number {
	return columns.findIndex((column) => !isReservedColumn(column.id));
}

export function isFirstNonReservedColumn(
	columns: Array<{ id: string }>,
	columnIndex: number,
): boolean {
	const firstNonReservedIndex = getFirstNonReservedColumnIndex(columns);
	return columnIndex === firstNonReservedIndex;
}
